import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AppStoreSoon.css';
import logo from '../assets/logo.png';
import appStoreBadge from '../assets/app-store-badge.png';

export default function AppStoreSoon() {
  const navigate = useNavigate();
  
  // State for email input and notification
  const [email, setEmail] = useState('');
  const [isNotifying, setIsNotifying] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState(null);

  // Email validation function
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Handle email input change
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (notificationStatus) {
      setNotificationStatus(null);
    }
  };

  // Handle notify me button click
  const handleNotifyMe = async () => {
    if (!email) {
      setNotificationStatus('error');
      alert('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setNotificationStatus('error');
      alert('Please enter a valid email address');
      return;
    }

    setIsNotifying(true);

    try {
      const response = await fetch('/api/notify-me', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: email,
          platform: 'safepalpay',
          timestamp: new Date().toISOString()
        }),
      });

      if (response.ok) {
        setNotificationStatus('success');
        alert('Thank you! We will notify you when SafepalPay launches on iOS.');
        setEmail('');
      } else {
        throw new Error('Failed to save email');
      }
    } catch (error) {
      console.error('Error saving email:', error);
      setNotificationStatus('error');
      alert('Something went wrong. Please try again later.');
    } finally {
      setIsNotifying(false);
    }
  };

  // Handle enter key press in email input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleNotifyMe();
    }
  };

  // Handle Google Play button click
  const handleGooglePlayClick = () => {
    if (window.gtag) {
      window.gtag('event', 'click_google_play', {
        'event_category': 'engagement',
        'event_label': 'SafepalPay Android Download'
      });
    }
    navigate('/safepalplaystore');
  };

  // Handle back button click
  const handleBackClick = () => {
    if (window.gtag) {
      window.gtag('event', 'click_back_home', {
        'event_category': 'navigation',
        'event_label': 'Back to Home from SafepalPay'
      });
    }
    navigate('/');
  };

  return (
    <div className="appstore-soon-container-unique">
      {/* Animated Background */}
      <div className="animated-bg-unique">
        <div className="circle-unique circle-1-unique"></div>
        <div className="circle-unique circle-2-unique"></div>
        <div className="circle-unique circle-3-unique"></div>
      </div>

      {/* Main Content */}
      <div className="appstore-content-unique">
        {/* Logo */}
        <div className="appstore-logo-unique">
          <img src={logo} alt="SafepalPay" />
        </div>

        {/* App Store Badge */}
        <div className="appstore-badge-container-unique">
          <img src={appStoreBadge} alt="App Store" className="appstore-badge-img-unique" />
        </div>

        {/* Main Heading */}
        <h1 className="appstore-title-unique">
          We're Working to Reach You
          <br />
          <span className="highlight-unique">on the App Store</span>
        </h1>

        {/* Description */}
        <p className="appstore-description-unique">
          SafepalPay is coming to iOS soon! We're putting the final touches on our App Store version 
          to bring you the best secure crypto payment experience on your iPhone and iPad.
        </p>

        {/* Features List */}
        <div className="appstore-features-unique">
          <div className="feature-item-unique">
            <div className="feature-icon-unique">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span>Secure & Fast Transactions</span>
          </div>
          <div className="feature-item-unique">
            <div className="feature-icon-unique">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15V17M6 21H18C19.1046 21 20 20.1046 20 19V13C20 11.8954 19.1046 11 18 11H6C4.89543 11 4 11.8954 4 13V19C4 20.1046 4.89543 21 6 21ZM16 11V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V11H16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <span>Bank-Level Security</span>
          </div>
          <div className="feature-item-unique">
            <div className="feature-icon-unique">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 10V3L4 14H11L11 21L20 10L13 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span>Lightning-Fast Processing</span>
          </div>
          <div className="feature-item-unique">
            <div className="feature-icon-unique">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span>24/7 Support</span>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="progress-section-unique">
          <div className="progress-label-unique">Development Progress</div>
          <div className="progress-bar-unique">
            <div className="progress-fill-unique"></div>
          </div>
          <div className="progress-percentage-unique">85% Complete</div>
        </div>

        {/* Notify Me Section */}
        <div className="notify-section-unique">
          <h3>Get Notified When We Launch</h3>
          <div className="notify-form-unique">
            <input 
              type="email" 
              placeholder="Enter your email address" 
              className="notify-input-unique"
              value={email}
              onChange={handleEmailChange}
              onKeyPress={handleKeyPress}
              disabled={isNotifying}
            />
            <button 
              className="notify-btn-unique"
              onClick={handleNotifyMe}
              disabled={isNotifying}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 17H20L18.5951 15.5951C18.2141 15.2141 18 14.6973 18 14.1585V11C18 8.38757 16.3304 6.16509 14 5.34142V5C14 3.89543 13.1046 3 12 3C10.8954 3 10 3.89543 10 5V5.34142C7.66962 6.16509 6 8.38757 6 11V14.1585C6 14.6973 5.78595 15.2141 5.40493 15.5951L4 17H9M15 17V18C15 19.6569 13.6569 21 12 21C10.3431 21 9 19.6569 9 18V17M15 17H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {isNotifying ? 'Subscribing...' : 'Notify Me'}
            </button>
          </div>
          {notificationStatus === 'success' && (
            <div className="notification-success-unique" style={{color: '#4caf50', marginTop: '10px', fontSize: '14px'}}>
              ✓ Subscribed successfully!
            </div>
          )}
        </div>

        {/* Available Now Section */}
        <div className="available-now-unique">
          <p>Available now on Android:</p>
          <button 
            className="google-play-link-unique"
            onClick={handleGooglePlayClick}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.609 1.814L13.792 12L3.61 22.186C3.218 21.867 3 21.377 3 20.809V3.191C3 2.623 3.218 2.133 3.609 1.814Z" fill="currentColor"/>
              <path d="M14.5 12.707L4.793 22.414L14.5 17.293L19.621 14.732C20.121 14.482 20.5 13.982 20.5 13.5C20.5 13.018 20.207 12.518 19.621 12.268L14.5 9.707V12.707Z" fill="currentColor"/>
              <path d="M4.793 1.586L14.5 11.293V6.707L9.379 4.146C8.879 3.896 8.293 3.793 7.707 3.793L4.793 1.586Z" fill="currentColor"/>
            </svg>
            Get it on Google Play
          </button>
        </div>

        {/* Back Button */}
        <button className="back-btn-unique" onClick={handleBackClick}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Home
        </button>
      </div>

      {/* Floating Particles */}
      <div className="particles-unique">
        <div className="particle-unique"></div>
        <div className="particle-unique"></div>
        <div className="particle-unique"></div>
        <div className="particle-unique"></div>
        <div className="particle-unique"></div>
      </div>
    </div>
  );
}